import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, HelpCircle, MessageCircle, RefreshCw, XCircle } from 'lucide-react';

const SignupCancelled = () => {
  const navigate = useNavigate();

  // Check if there's pending signup data
  const pendingSignup = sessionStorage.getItem('pendingSignup');
  const hasPendingData = !!pendingSignup;

  const handleRetry = () => {
    // If we have pending data, go back to the signup form
    // Otherwise, go to pricing page
    if (hasPendingData) {
      navigate('/business-trial');
    } else {
      navigate('/pricing');
    }
  };

  const handleContactSupport = () => {
    // You can replace this with your actual support contact method
    window.location.href = 'mailto:support@hivetivity.com?subject=Help with signup';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-lg"
      >
        <Card className="shadow-xl border-gray-200">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl">Signup Cancelled</CardTitle>
            <CardDescription className="text-base">
              No worries! Your information is safe and no charges were made.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Your free trial is still available!</strong> You can return anytime to complete your signup and start your 14-day trial.
              </p>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={handleRetry}
                className="w-full"
                size="lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {hasPendingData ? 'Continue Signup' : 'Try Again'}
              </Button>

              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </div>

            {/* Help section */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-gray-600" />
                Need Help?
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>Common reasons for cancellation:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Questions about pricing or features</li>
                  <li>Technical issues during signup</li>
                  <li>Need more time to decide</li>
                </ul>
              </div>
              
              <Button
                onClick={handleContactSupport}
                variant="link"
                className="mt-4 p-0 h-auto text-[#FA8C00] hover:text-[#FA8C00]/80"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Additional info */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center text-sm text-gray-500 mt-6"
        >
          No payment information was collected. Your trial hasn't started yet.
        </motion.p>
      </motion.div>
    </div>
  );
};

export default SignupCancelled;